import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Box,
  Divider,
  Chip,
  TablePagination,
  Paper,
  Alert,
} from '@mui/material';
import {
  getOtherExpenseMargins,
  deleteOtherExpensesMargin,
  OtherExpensesMargin,
  OtherExpenseCategoryOption,
  otherExpenseCategoryKey,
} from 'api/otherExpensesApi';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import ConfirmDeleteDialog from 'components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import { useSnackbar } from 'notistack';

interface OtherExpensesMarginDetailsViewProps {
  handleClose: () => void;
  selectedCategory?: OtherExpenseCategoryOption;
  open: boolean;
  onEdit?: (rate: OtherExpensesMargin) => void;
}

export default function OtherExpensesMarginDetailsView({
  handleClose,
  selectedCategory,
  open,
  onEdit,
}: OtherExpensesMarginDetailsViewProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<OtherExpensesMargin | null>(
    null
  );

  const queryClient = useQueryClient();
  const { data: otherExpensesData } = useQuery(
    ['other-expenses-margin'],
    getOtherExpenseMargins
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteMutation = useMutation(deleteOtherExpensesMargin, {
    onSuccess: () => {
      queryClient.invalidateQueries(['other-expenses-margin']);
      enqueueSnackbar('Other expenses margin deleted successfully', {
        variant: 'success',
      });
    },
    onError: () => {
      enqueueSnackbar('Failed to delete other expenses margin', {
        variant: 'error',
      });
    },
  });

  const handleDelete = async (rateId: string) => {
    await deleteMutation.mutateAsync(rateId);
    setDeleteDialogOpen(false);
  };

  const displayRates =
    otherExpensesData?.filter(
      (r) =>
        otherExpenseCategoryKey(r.otherExpenseType, r.otherExpenseCategoryId) ===
        selectedCategory?.key
    ) || [];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            my: 1,
            display: 'flex',
            justifyContent: 'space-between',
          }}>
          <Box display="flex" alignItems="center">
            <Typography variant="h6">Other Expenses Margin History</Typography>
            {selectedCategory && (
              <Chip
                label={selectedCategory.label}
                size="small"
                sx={{ ml: 2, backgroundColor: 'primary.main', color: 'white' }}
              />
            )}
          </Box>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            mb: 2,
          }}>
          <Box>
            <Paper elevation={0}>
              <Table size="medium">
                <TableHead>
                  <TableRow>
                    <TableCell>Margin (%)</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Branch</TableCell>
                    <TableCell>Effective From</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRates.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography align="center" variant="body2">
                          No margin history available.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {displayRates
                        .sort((a, b) => {
                          if (a.isDefault && !b.isDefault) return -1;
                          if (!a.isDefault && b.isDefault) return 1;
                          return (
                            new Date(b.effectiveFrom).getTime() -
                            new Date(a.effectiveFrom).getTime()
                          );
                        })
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((rate) => (
                          <TableRow key={rate.id}>
                            <TableCell>
                              {Number(rate.margin).toFixed(2)}%
                              {rate.isDefault && (
                                <Chip
                                  label="Default"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    backgroundColor: 'grey.500',
                                    color: 'white',
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {rate.client?.legalName || '-'}
                            </TableCell>
                            <TableCell>{rate.branch?.name || '-'}</TableCell>
                            <TableCell>
                              {format(
                                new Date(rate.effectiveFrom),
                                'dd MMM yyyy'
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {onEdit && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(rate);
                                  }}
                                  aria-label="Edit margin">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              )}
                              <IconButton
                                size="small"
                                sx={{ ml: 1 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItem(rate);
                                  setDeleteDialogOpen(true);
                                }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    </>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={displayRates.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
          </Box>
        </DialogContent>
      </Dialog>
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        title="Delete Confirmation"
        content={
          <>
            Are you sure you want to delete the margin for{' '}
            <b>{selectedCategory?.label ?? ''}</b>
            ?
            <Alert severity="warning" style={{ marginTop: '1rem' }}>
              This action is irreversible and will permanently delete the data.
            </Alert>
          </>
        }
        deleteFunc={async () => {
          await handleDelete(selectedItem?.id);
        }}
        handleReject={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
        handleClose={() => {
          setDeleteDialogOpen(false);
          setSelectedItem(null);
        }}
      />
    </>
  );
}
